import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import Logo from '@prisjakt/ui-logo';
import T from '@prisjakt/ui-translate';
import { breakpoints } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import { withViewport } from '@prisjakt/ui-viewport';

import SearchBar from '../../components/search-bar';
import Viewport from '../../components/viewport';
import searchActions from '../search/actions/search';
import menuActions from '../navigation/actions/desktop';
import mobileMenuActions from '../navigation/actions/mobile-menu';
import NavigationMenu from '../navigation/index';
import MobileMenuButton from '../navigation/components/mobile-menu-button';
import ClassicSwitch from '../../components/classic-switch';
import NoScroll from '../../components/no-scroll';

import Suggestions from './components/suggestions';

import * as S from './styles';

class Header extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      ...Header.getStateFromLocation(this.props.location),
      query: '',
    };
    this.state.showSearchBar = false;

    this.toggleSearchBar = this.toggleSearchBar.bind(this);
    this.closeSearchBar = this.closeSearchBar.bind(this);

    this.megaMenuButtonRef = React.createRef();
  }

  componentDidMount() {
    this.historyChangeListener = this.props.history.listen(location => {
      this.setState(Header.getStateFromLocation(location));
    });
  }

  static getStateFromLocation(location) {
    return {
      isFrontpage: location.pathname === '/',
      isSearchPage: location.pathname.indexOf('/search') === 0,
      searchFocused: /search=/.test(location.search),
    };
  }

  componentWillUnmount() {
    this.historyChangeListener();
  }

  componentDidUpdate(prevProps) {
    // Hide the search bar when changing route on mobile
    if (
      this.props.location !== prevProps.location &&
      this.props.history.action === 'PUSH' &&
      !this.state.searchFocused &&
      !this.state.isSearchPage
    ) {
      this.closeSearchBar();
    }
  }

  static shouldAutoOpenSidebar() {
    if (!window.matchMedia) {
      return false;
    }

    if (window.matchMedia(breakpoints.largeUp).matches) {
      return true;
    }

    return false;
  }

  toggleSearchBar() {
    this.setState(prevState => ({
      showSearchBar: !prevState.showSearchBar,
    }));

    if (this.props.mobileMenuOpen) {
      this.props.closeMenu();
    }
  }

  closeSearchBar() {
    this.setState({
      showSearchBar: false,
      query: '',
    });
  }

  toggleMegaMenu = e => {
    // Prevent event from triggering an "outside click" for the menu
    e.stopPropagation();

    this.props.toggleMenu({ open: !this.props.megaMenuOpen });
  };

  onCloseMegaMenu = () => {
    if (this.megaMenuButtonRef.current) {
      this.megaMenuButtonRef.current.focus();
    }
  };

  searchBarOnInputChange = text => this.setState({ query: text });

  clearQuery = () => {
    this.setState({ query: '' });
  };

  render() {
    const { mobileMenuOpen, search, menu, viewport, megaMenuOpen } = this.props;

    const { t, market, renderMode } = this.context;
    const {
      searchFocused,
      isFrontpage,
      isSearchPage,
      showSearchBar,
    } = this.state;

    const isSidebarOpen = mobileMenuOpen;
    const isScrolledDown = viewport && viewport.top > 90;
    const reserveSpaceForExpandedHeader = isFrontpage;
    const isExpandedOnMobile = isFrontpage && !isSidebarOpen && !isScrolledDown;
    const isMobileSearchOpen =
      (showSearchBar || isSearchPage || (isFrontpage && !isScrolledDown)) &&
      !isSidebarOpen;

    const menuButtonTitle = megaMenuOpen
      ? t('symbols', 'symbol.close')
      : t('header', 'categories.title');

    return (
      <>
        <S.FixedMargin
          mobileSearchOpen={isMobileSearchOpen || reserveSpaceForExpandedHeader}
          expandedOnMobile={isExpandedOnMobile || reserveSpaceForExpandedHeader}
        />
        <S.HeaderBar
          fixed
          searchFocused={searchFocused}
          expandedOnMobile={isExpandedOnMobile}
          mobileSearchOpen={isMobileSearchOpen}
        >
          <S.Wrapper>
            <S.MenuColumn tablet mobile data-test="MobileMenuColumn">
              <S.MenuItem>{menu && <MobileMenuButton />}</S.MenuItem>
            </S.MenuColumn>

            <S.LogoColumn collapsed={searchFocused}>
              <S.MegaMenuItem title={menuButtonTitle} placement="right">
                <S.MegaMenuButton
                  type="button"
                  onClick={this.toggleMegaMenu}
                  aria-label={menuButtonTitle}
                  active={megaMenuOpen}
                  aria-expanded={megaMenuOpen}
                  aria-haspopup
                  ref={this.megaMenuButtonRef}
                  data-test="MegaMenuToggleButton"
                >
                  {megaMenuOpen ? (
                    <S.CloseMegaMenuIcon size={30} />
                  ) : (
                    <S.MegaMenuIcon size={30} />
                  )}
                </S.MegaMenuButton>
              </S.MegaMenuItem>

              <S.LogoLink
                to="/"
                onClick={() =>
                  this.props.mobileMenuOpen && this.props.closeMenu()
                }
              >
                <Logo
                  market={market.code}
                  companyName={t('meta', 'market.logo-alt')}
                  styling={renderMode === 'beta' ? 'beta' : 'light'}
                />
                <Viewport tablet desktop>
                  <S.PromoText>{t('header', 'logo.promo-text')}</S.PromoText>
                </Viewport>
              </S.LogoLink>
            </S.LogoColumn>

            <S.SearchColumn tablet desktop expanded={searchFocused}>
              {search && (
                <SearchBar
                  autoFocus={isFrontpage}
                  onFocusChange={this.props.searchFocus}
                  onBlurChange={this.props.searchBlur}
                  focused
                  search={this.props.location.search}
                  onInputChange={this.searchBarOnInputChange}
                />
              )}
            </S.SearchColumn>

            <S.RightColumn collapsed={searchFocused}>
              <S.FadingBox visible={!isMobileSearchOpen}>
                <Viewport mobile>
                  <S.MenuItem>
                    <button
                      style={{ height: 50 }}
                      type="button"
                      onClick={this.toggleSearchBar}
                      aria-label={t('header', 'search.button')}
                    >
                      <S.SearchIcon size={30} />
                    </button>
                  </S.MenuItem>
                </Viewport>
              </S.FadingBox>

              <Viewport
                desktop
                style={{ marginRight: 'auto', marginLeft: 'auto' }}
              >
                <ClassicSwitch utmMedium="header">
                  <S.ClassicSwitchLink>
                    <T domain="header">login.classic-link-word-wrap</T>
                  </S.ClassicSwitchLink>
                </ClassicSwitch>
              </Viewport>

              <Viewport tablet>
                <S.MenuItem wideOnTablet>
                  {menu && (
                    <MobileMenuButton handleOnClick={this.closeSearchBar} />
                  )}
                </S.MenuItem>
              </Viewport>
            </S.RightColumn>
          </S.Wrapper>
          <S.SearchBox visible={isMobileSearchOpen}>
            <S.SearchColumn mobile>
              {search && (
                <SearchBar
                  search={this.props.location.search}
                  onFocusChange={this.props.searchFocus}
                  onBlurChange={this.props.searchBlur}
                  autoFocusOnExpand={showSearchBar && !isFrontpage}
                  onInputChange={this.searchBarOnInputChange}
                  mobile
                />
              )}
              {isExpandedOnMobile && (
                <S.PromoText>{t('header', 'logo.promo-text')}</S.PromoText>
              )}
            </S.SearchColumn>
          </S.SearchBox>

          {this.state.query && (
            <Suggestions
              query={this.state.query}
              searchInProps={false}
              onClose={this.clearQuery}
            />
          )}
          <NoScroll mobile enabled={!!this.state.query} />
        </S.HeaderBar>

        {menu && (
          <NavigationMenu
            isFrontpage={isFrontpage}
            onCloseMegaMenu={this.onCloseMegaMenu}
          />
        )}
      </>
    );
  }
}

Header.propTypes = {
  mobileMenuOpen: PropTypes.bool,
  megaMenuOpen: PropTypes.bool,
  megaMenuOpenCategory: PropTypes.string,
  sidebarOpen: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  menu: PropTypes.bool,
  search: PropTypes.bool,
  toggleMenu: PropTypes.func.isRequired,
  closeMenu: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    listen: PropTypes.func,
    action: PropTypes.string,
  }).isRequired,
  searchInputQuery: PropTypes.func.isRequired,
  searchFocus: PropTypes.func.isRequired,
  searchBlur: PropTypes.func.isRequired,
  viewport: PropTypes.shape({
    top: PropTypes.number,
  }),
};

Header.defaultProps = {
  menu: true,
  search: true,
  mobileMenuOpen: false,
  megaMenuOpen: false,
  megaMenuOpenCategory: null,
  sidebarOpen: false,
  isAuthenticated: false,
  viewport: {},
};

Header.contextType = AppContext;

export default connect(
  state => ({
    mobileMenuOpen: state.areas.navigation.mobileMenu.open,
    megaMenuOpen: state.areas.navigation.megaMenu.open,
    megaMenuOpenCategory: state.areas.navigation.megaMenu.openCategory,
    sidebarOpen: state.areas.sidebar.open,
    isAuthenticated: state.areas.account.isAuthenticated,
  }),
  {
    ...searchActions,
    ...mobileMenuActions,
    ...menuActions,
  }
)(withRouter(withViewport(Header)));
